import React, { useState, useCallback, useEffect } from 'react'
import { toast } from 'react-toastify'
import useAudioRecorder from './useAudioRecorder'
import useVoiceTranscription from './useVoiceTranscription'
import VoiceAnimation, { VoiceAnimationState } from './VoiceAnimation'

interface VoiceInputProps {
  onTranscriptReady?: (transcript: string) => void
  onSubmit?: (transcript: string) => void
  disabled?: boolean
  className?: string
  showAnimation?: boolean
}

const VoiceInput: React.FC<VoiceInputProps> = ({
  onTranscriptReady,
  onSubmit,
  disabled = false,
  className = '',
  showAnimation = true,
}) => {
  const [animationState, setAnimationState] = useState<VoiceAnimationState>('idle')

  // Handle silence detected - stop recording and process
  const handleSilenceDetected = useCallback(() => {
    stopRecording()
  }, [])

  const {
    isRecording,
    audioBlob,
    error: recorderError,
    startRecording,
    stopRecording,
    resetRecording,
  } = useAudioRecorder({
    silenceThreshold: 15,
    silenceDuration: 2000,
    onSilenceDetected: handleSilenceDetected,
  })

  const {
    isTranscribing,
    error: transcriptionError,
    transcribe,
    resetTranscript,
  } = useVoiceTranscription()

  // Update animation state based on current state
  useEffect(() => {
    if (isRecording) {
      setAnimationState('listening')
    } else if (isTranscribing) {
      setAnimationState('processing')
    } else {
      setAnimationState('idle')
    }
  }, [isRecording, isTranscribing])

  // Process audio blob when recording stops
  useEffect(() => {
    const processAudio = async () => {
      if (audioBlob && !isRecording && !isTranscribing) {
        const transcript = await transcribe(audioBlob)
        if (transcript) {
          onTranscriptReady?.(transcript)
          // Auto-submit after transcription
          if (onSubmit) {
            onSubmit(transcript)
          }
        }
        resetRecording()
      }
    }
    processAudio()
  }, [audioBlob, isRecording, isTranscribing, transcribe, onTranscriptReady, onSubmit, resetRecording])

  // Show error toasts
  useEffect(() => {
    if (recorderError) {
      toast.error(recorderError)
    }
  }, [recorderError])

  useEffect(() => {
    if (transcriptionError) {
      toast.error(transcriptionError)
    }
  }, [transcriptionError])

  const handleClick = useCallback(async () => {
    if (disabled) return

    if (isRecording) {
      stopRecording()
    } else {
      resetTranscript()
      resetRecording()
      await startRecording()
    }
  }, [disabled, isRecording, stopRecording, startRecording, resetTranscript, resetRecording])

  const isActive = isRecording || isTranscribing

  return (
    <div className={`tw-relative tw-inline-flex tw-items-center tw-justify-center ${className}`}>
      {/* Animation overlay when active */}
      {showAnimation && animationState !== 'idle' && (
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-z-10">
          <VoiceAnimation state={animationState} size="sm" />
        </div>
      )}

      {/* Microphone button */}
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        className={`
          tw-relative tw-z-20 tw-flex tw-items-center tw-justify-center
          tw-w-10 tw-h-10 tw-rounded-full tw-transition-all tw-duration-200
          ${disabled
            ? 'tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed'
            : isActive
            ? 'tw-bg-transparent tw-text-white'
            : 'tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-gray-200 hover:tw-text-gray-800'
          }
        `}
        title={isRecording ? 'Stop recording' : isTranscribing ? 'Processing...' : 'Click to speak'}
      >
        {isTranscribing ? (
          // Processing spinner
          <span className="fa fa-spinner fa-spin" />
        ) : isRecording ? (
          // Stop icon when recording
          <span className="fa fa-stop" />
        ) : (
          // Microphone icon when idle
          <span className="fa fa-microphone" />
        )}
      </button>
    </div>
  )
}

export default VoiceInput
